import { FC, useMemo, SetStateAction } from 'react';
import { Stack, MenuItem, TextField, Typography, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FilterOperator, FilterCondition } from '@/types/entities/marketing-list';
import Iconify from '@/components/media/iconify';

type Props = {
  name: string;
  label: string;
  filters: Record<string, any>;
  onChange: (value: SetStateAction<any>) => void;
  required?: boolean;
  allowedOperators?: FilterOperator[];
};

/**
 * Advanced text filter component
 * Allows user to select operator (equals, contains, isEmpty, etc.) and provide value
 */
export const AdvancedTextFilter: FC<Props> = ({
  name,
  label,
  filters,
  onChange,
  required = false,
  allowedOperators = [
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.CONTAINS,
    FilterOperator.NOT_CONTAINS,
    FilterOperator.STARTS_WITH,
    FilterOperator.ENDS_WITH,
    FilterOperator.IS_EMPTY,
    FilterOperator.IS_NOT_EMPTY,
  ],
}) => {
  const { t } = useTranslation();

  const condition: FilterCondition<string> | undefined = useMemo(
    () => filters?.advancedFilters?.[name],
    [filters, name]
  );

  const currentOperator = condition?.operator || FilterOperator.CONTAINS;
  const currentValue = condition?.value || '';

  // Operators that don't require a value
  const noValueOperators = [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY];
  const requiresValue = !noValueOperators.includes(currentOperator as FilterOperator);

  const handleOperatorChange = (operator: string) => {
    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      advancedFilters: {
        ...(prevState.advancedFilters || {}),
        [name]: {
          operator,
          value: noValueOperators.includes(operator as FilterOperator) ? undefined : currentValue,
        },
      },
    }));
  };

  const handleValueChange = (value: string) => {
    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      advancedFilters: {
        ...(prevState.advancedFilters || {}),
        [name]: {
          operator: currentOperator,
          value,
        },
      },
    }));
  };

  const handleClear = () => {
    onChange((prevState: Record<string, any>) => {
      const { [name]: removed, ...rest } = prevState.advancedFilters || {};
      return {
        ...prevState,
        advancedFilters: rest,
      };
    });
  };

  const getOperatorLabel = (operator: FilterOperator): string => {
    const labels: Record<FilterOperator, string> = {
      [FilterOperator.EQUALS]: t('Equals'),
      [FilterOperator.NOT_EQUALS]: t('Not equals'),
      [FilterOperator.CONTAINS]: t('Contains'),
      [FilterOperator.NOT_CONTAINS]: t('Does not contain'),
      [FilterOperator.STARTS_WITH]: t('Starts with'),
      [FilterOperator.ENDS_WITH]: t('Ends with'),
      [FilterOperator.IS_EMPTY]: t('Is empty'),
      [FilterOperator.IS_NOT_EMPTY]: t('Is not empty'),
      [FilterOperator.IN]: t('In'),
      [FilterOperator.NOT_IN]: t('Not in'),
    };
    return labels[operator] || operator;
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{label}</Typography>
        {!required && condition && (
          <IconButton size="small" onClick={handleClear} sx={{ ml: 1 }}>
            <Iconify icon="solar:close-circle-bold" width={18} />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <TextField
          select
          size="small"
          value={currentOperator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {allowedOperators.map((operator) => (
            <MenuItem key={operator} value={operator}>
              {getOperatorLabel(operator)}
            </MenuItem>
          ))}
        </TextField>

        {requiresValue && (
          <TextField
            fullWidth
            size="small"
            placeholder={t('Enter value')}
            value={currentValue}
            onChange={(e) => handleValueChange(e.target.value)}
          />
        )}
      </Stack>
    </Stack>
  );
};
